import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  useGetBankDocumentByIdQuery,
  useLazyGetBankDocumentPostingStatusQuery,
  usePostBankDocumentBatchMutation,
  usePostBankDocumentEntryMutation,
  useUnpostBankDocumentEntryMutation,
} from '../Service/bankDocumentsService';
import { EntryFilter } from './useBankDocumentDetails.types';
import { mapBankDocumentDtoToModel } from '../Mapping/bankDocumentsMapper';
import {
  getBankDocumentsErrorCode,
  getBankDocumentsErrorMessage,
} from '../Utils/bankDocumentsErrorMapping';
import { bankDocumentsLogger } from '../Utils/bankDocumentsLogger';
import {
  buildPostBankDocumentBatchPayload,
  buildPostBankDocumentEntryRequest,
} from '../Service/bankDocumentsRequestBuilder';
import { PostBankDocumentBatchResultDto } from '../Types/bankDocument.types';

interface UseBankDocumentDetailsArgs {
  bankDocumentId: string | undefined;
  userId: number;
}

interface UseBankDocumentDetailsResult {
  parsedId: number;
  isInvalidId: boolean;
  bankDocument: ReturnType<typeof mapBankDocumentDtoToModel> | null;
  isLoading: boolean;
  isError: boolean;
  isPosting: boolean;
  errorMessage: string;
  entryFilter: EntryFilter;
  setEntryFilter: Dispatch<SetStateAction<EntryFilter>>;
  focusedEntryId: number | null;
  filteredEntries: ReturnType<typeof mapBankDocumentDtoToModel>['entries'];
  postingDescription: string;
  setPostingDescription: Dispatch<SetStateAction<string>>;
  postingSuccessMessage: string | null;
  postingErrorMessage: string | null;
  batchSuccessMessage: string | null;
  batchErrorMessage: string | null;
  isBatchProcessing: boolean;
  postingDiagnostics: PostBankDocumentBatchResultDto | null;
  focusEntry: (entryId: number) => boolean;
  handlePostSingleEntry: (entryId: number) => Promise<void>;
  handleUnpostSingleEntry: (entryId: number) => Promise<void>;
  handleQuickNextUnposted: () => void;
  handlePostBatch: () => Promise<void>;
  handleRefreshPostingStatus: () => Promise<void>;
}

export const useBankDocumentDetails = ({
  bankDocumentId,
  userId,
}: UseBankDocumentDetailsArgs): UseBankDocumentDetailsResult => {
  const parsedId = Number(bankDocumentId);
  const isInvalidId = !Number.isInteger(parsedId) || parsedId <= 0;

  const [entryFilter, setEntryFilter] = useState<EntryFilter>('all');
  const [focusedEntryId, setFocusedEntryId] = useState<number | null>(null);
  const [pendingNextFromEntryId, setPendingNextFromEntryId] = useState<number | null>(null);
  const [postingDescription, setPostingDescription] = useState<string>('');
  const [postingSuccessMessage, setPostingSuccessMessage] = useState<string | null>(null);
  const [postingErrorMessage, setPostingErrorMessage] = useState<string | null>(null);
  const [batchSuccessMessage, setBatchSuccessMessage] = useState<string | null>(null);
  const [batchErrorMessage, setBatchErrorMessage] = useState<string | null>(null);
  const [postingDiagnostics, setPostingDiagnostics] =
    useState<PostBankDocumentBatchResultDto | null>(null);

  const { data, isLoading, isError, error, refetch } = useGetBankDocumentByIdQuery(parsedId, {
    skip: isInvalidId,
  });
  const [postBankDocumentEntry, { isLoading: isPosting }] = usePostBankDocumentEntryMutation();
  const [postBankDocumentBatch, { isLoading: isBatchPosting }] = usePostBankDocumentBatchMutation();
  const [fetchPostingStatus, { isFetching: isFetchingPostingStatus }] =
    useLazyGetBankDocumentPostingStatusQuery();
  const [unpostBankDocumentEntry, { isLoading: isUnposting }] =
    useUnpostBankDocumentEntryMutation();

  const bankDocument = useMemo(() => (data ? mapBankDocumentDtoToModel(data) : null), [data]);

  const focusEntry = useCallback(
    (entryId: number): boolean => {
      if (!entryId) {
        return false;
      }

      setFocusedEntryId(entryId);
      if (entryFilter !== 'all') {
        setEntryFilter('all');
      }

      window.setTimeout(() => {
        document
          ?.getElementById(`bank-document-entry-row-${entryId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);

      return true;
    },
    [entryFilter]
  );

  const goToNextUnposted = useCallback(
    (fromEntryId?: number): boolean => {
      const entries = bankDocument?.entries ?? [];

      if (entries.length === 0) {
        return false;
      }

      let nextEntry = null as (typeof entries)[number] | null;

      if (fromEntryId) {
        const currentIndex = entries.findIndex((entry) => entry.id === fromEntryId);

        if (currentIndex >= 0) {
          nextEntry = entries.slice(currentIndex + 1).find((entry) => !entry.isPosted) ?? null;
        }
      }

      if (!nextEntry) {
        nextEntry = entries.find((entry) => !entry.isPosted) ?? null;
      }

      if (!nextEntry) {
        return false;
      }

      return focusEntry(nextEntry.id);
    },
    [bankDocument?.entries, focusEntry]
  );

  useEffect(() => {
    if (pendingNextFromEntryId === null) {
      return;
    }

    const moved = goToNextUnposted(pendingNextFromEntryId);
    if (!moved) {
      setPostingSuccessMessage('Wszystkie pozycje dokumentu są już zaksięgowane.');
      setFocusedEntryId(null);
    }

    setPendingNextFromEntryId(null);
  }, [goToNextUnposted, pendingNextFromEntryId]);

  const filteredEntries = useMemo(() => {
    const entries = bankDocument?.entries ?? [];

    if (entryFilter === 'posted') {
      return entries.filter((entry) => entry.isPosted);
    }

    if (entryFilter === 'unposted') {
      return entries.filter((entry) => !entry.isPosted);
    }

    return entries;
  }, [bankDocument?.entries, entryFilter]);

  const errorMessage = getBankDocumentsErrorMessage(
    error,
    'Nie udało się pobrać szczegółów dokumentu bankowego.'
  );

  const handlePostSingleEntry = async (entryId: number) => {
    setPostingErrorMessage(null);
    setPostingSuccessMessage(null);
    setBatchErrorMessage(null);

    try {
      bankDocumentsLogger.debug('[BankDocuments] Post single entry started', {
        bankDocumentId: parsedId,
        entryId,
      });

      const request = buildPostBankDocumentEntryRequest({
        entryId,
        userId,
      });

      const wasAlreadyPosted =
        bankDocument?.entries?.some((entry) => entry.id === entryId && entry.isPosted) ?? false;

      const result = await postBankDocumentEntry(request).unwrap();

      const statusMessage = wasAlreadyPosted
        ? `Pozycja ${entryId} była już wcześniej zaksięgowana.`
        : `Pozycja ${entryId} została zaksięgowana.`;

      setPostingSuccessMessage(statusMessage);
      await refetch();
      setPendingNextFromEntryId(entryId);

      bankDocumentsLogger.debug('[BankDocuments] Post single entry success', {
        bankDocumentId: parsedId,
        entryId,
        bankDocumentEntryId: result.bankDocumentEntryId,
        wasAlreadyPosted,
        invoiceId: result.invoiceId,
        reservationId: result.reservationId,
        expenseInvoiceId: result.expenseInvoiceId,
        invoicePaymentId: result.invoicePaymentId,
        reservationPaymentId: result.reservationPaymentId,
      });
    } catch (postingError) {
      const mappedMessage = getBankDocumentsErrorMessage(
        postingError,
        'Nie udało się zaksięgować pozycji dokumentu.'
      );
      const errorCode = getBankDocumentsErrorCode(postingError);

      setPostingErrorMessage(mappedMessage);

      bankDocumentsLogger.error('[BankDocuments] Post single entry failed', {
        bankDocumentId: parsedId,
        entryId,
        errorCode,
        mappedMessage,
        postingError,
      });
    }
  };

  const handleQuickNextUnposted = () => {
    setPostingErrorMessage(null);
    setPostingSuccessMessage(null);

    const moved = goToNextUnposted(focusedEntryId ?? undefined);
    if (!moved) {
      setPostingErrorMessage('Brak niezaksięgowanych pozycji do przejścia.');
      bankDocumentsLogger.warn('[BankDocuments] Next unposted requested but queue is empty', {
        bankDocumentId: parsedId,
      });
    }
  };

  const handleUnpostSingleEntry = async (entryId: number) => {
    setPostingErrorMessage(null);
    setPostingSuccessMessage(null);
    setBatchErrorMessage(null);

    try {
      await unpostBankDocumentEntry({ entryId, bankDocumentId: parsedId }).unwrap();
      setPostingSuccessMessage(`Cofnięto księgowanie pozycji ${entryId}.`);
      await refetch();
    } catch (unpostError) {
      setPostingErrorMessage(
        getBankDocumentsErrorMessage(
          unpostError,
          'Nie udało się cofnąć księgowania pozycji dokumentu.'
        )
      );
    }
  };

  const handlePostBatch = async () => {
    if (isInvalidId || !userId) {
      setBatchErrorMessage(
        'Nie udało się uruchomić batch księgowania: nieprawidłowe dane wejściowe.'
      );
      return;
    }

    setPostingErrorMessage(null);
    setPostingSuccessMessage(null);
    setBatchErrorMessage(null);
    setBatchSuccessMessage(null);

    try {
      const payload = buildPostBankDocumentBatchPayload(parsedId, userId);
      const result = await postBankDocumentBatch(payload).unwrap();

      setPostingDiagnostics(result);
      setBatchSuccessMessage(
        `Batch zakończony: posted ${result.postedCount}, skipped ${result.skippedCount}, failed ${result.failedCount}.`
      );

      await refetch();
    } catch (batchError) {
      setBatchErrorMessage(
        getBankDocumentsErrorMessage(
          batchError,
          'Nie udało się wykonać batch księgowania dokumentu.'
        )
      );

      bankDocumentsLogger.error('[BankDocuments] Post batch failed', {
        bankDocumentId: parsedId,
        errorCode: getBankDocumentsErrorCode(batchError),
        batchError,
      });
    }
  };

  const handleRefreshPostingStatus = async () => {
    if (isInvalidId) {
      setBatchErrorMessage('Nieprawidłowy identyfikator dokumentu bankowego.');
      return;
    }

    setBatchErrorMessage(null);
    setBatchSuccessMessage(null);

    try {
      const result = await fetchPostingStatus(parsedId).unwrap();
      setPostingDiagnostics(result);
      setBatchSuccessMessage(
        `Status odświeżony: posted ${result.postedCount}, skipped ${result.skippedCount}, failed ${result.failedCount}.`
      );
    } catch (statusError) {
      setBatchErrorMessage(
        getBankDocumentsErrorMessage(statusError, 'Nie udało się pobrać statusu księgowania.')
      );

      bankDocumentsLogger.error('[BankDocuments] Fetch posting status failed', {
        bankDocumentId: parsedId,
        errorCode: getBankDocumentsErrorCode(statusError),
        statusError,
      });
    }
  };

  return {
    parsedId,
    isInvalidId,
    bankDocument,
    isLoading,
    isError,
    isPosting: isPosting || isUnposting,
    errorMessage,
    entryFilter,
    setEntryFilter,
    focusedEntryId,
    filteredEntries,
    postingDescription,
    setPostingDescription,
    postingSuccessMessage,
    postingErrorMessage,
    batchSuccessMessage,
    batchErrorMessage,
    isBatchProcessing: isBatchPosting || isFetchingPostingStatus,
    postingDiagnostics,
    focusEntry,
    handlePostSingleEntry,
    handleUnpostSingleEntry,
    handleQuickNextUnposted,
    handlePostBatch,
    handleRefreshPostingStatus,
  };
};
